import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, bumpAuthEpoch, errMessage } from "../lib/api";

const AuthContext = createContext(null);

// Signs out on the dot when the hour-long session ends (the cookie has already expired by then).
const useSessionClock = (user, endsAt, signedOut) => {
  useEffect(() => {
    if (!user || !endsAt) return undefined;
    const t = setTimeout(() => {
      signedOut();
      toast.message("You’ve been signed out after an hour. Sign in again to carry on.");
    }, Math.max(0, new Date(endsAt).getTime() - Date.now()));
    return () => clearTimeout(t);
  }, [user, endsAt, signedOut]);
};

const useAuthActions = (setUser, setEndsAt) => {
  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      setEndsAt(data.session_ends_at || null);
    } catch {
      setUser(false);
    }
  }, [setUser, setEndsAt]);

  const login = useCallback(async (email, password, turnstileToken) => {
    const { data } = await api.post("/auth/login", { email, password, turnstile_token: turnstileToken || undefined });
    bumpAuthEpoch();
    setUser(data.user);
    setEndsAt(data.session_ends_at || null);
    return data.user;
  }, [setUser, setEndsAt]);

  const register = useCallback(async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    bumpAuthEpoch();
    await refresh();
    return data;
  }, [refresh]);

  const signedOut = useCallback(() => {
    bumpAuthEpoch();
    setUser(false);
    setEndsAt(null);
  }, [setUser, setEndsAt]);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      console.warn("Sign-out request failed; signing out on this device anyway", err);
    }
    signedOut();
  }, [signedOut]);

  return useMemo(() => ({ refresh, login, register, signedOut, logout }), [refresh, login, register, signedOut, logout]);
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = signed out
  const [endsAt, setEndsAt] = useState(null);
  const actions = useAuthActions(setUser, setEndsAt);

  useEffect(() => {
    actions.refresh();
  }, [actions]);

  useSessionClock(user, endsAt, actions.signedOut);

  const value = useMemo(() => ({ user, setUser, ...actions, errMessage }), [user, actions]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
